import React, { useState } from "react";
import { useDatastore } from "util/datastore";
import { colorAmbigousBlack, colorAmbiguousLightGrey, colorAmbiguousWhite, colorBorderPrimary, colorSurfacePrimary, colorTextPrimary, colorTextWarning } from 'component/color';
import { StyleSheet, View } from "react-native";
import { CTAButton } from 'component/button';
import { Close } from "@carbon/icons-react";
import { AutoSizeTextInput, fontFamilySansRegular, UtilityText } from 'component/text';
import { BLOCKLIST_CHARACTER_LIMIT } from "./blocklistConstants";
import { useTranslation } from "component/translation";

const BlocklistTextInputWithChipsStyle = StyleSheet.create<any>({
    inputContainer: {
        flexDirection: "column",
        borderWidth: 1,
        borderRadius: 4,
        backgroundColor: colorSurfacePrimary,
        borderColor: colorBorderPrimary,
    },
    inputContainerFocus: {
        outlineStyle: "solid",
        outlineWidth: 1,
        borderColor: colorTextPrimary,
        outlineColor: colorTextPrimary,
    },
    inputContainerError: {
        borderColor: colorTextWarning,
        outlineColor: colorTextWarning,
    },
    termsContainer: {
        maxHeight: 106,
        marginTop: 4,
        overflowY: "auto",
        overflowX: "hidden",
    },
    termsContainerInner: {
        flexDirection: "row",
        flexFlow: "wrap",
        paddingBottom: 10,
        paddingLeft: 4,
        paddingRight: 4
    },
    textField: {
        backgroundColor: colorSurfacePrimary,
        paddingHorizontal: 16,
        paddingVertical: 15,
        fontSize: 16,
        lineHeight: 24,
        fontFamily: fontFamilySansRegular,
        borderRadius: 4,
        borderWidth: 0,
        outlineWidth: 0,
    },
    termContainer: {
        paddingTop: 4,
        paddingLeft: 4,
        paddingRight: 4
    },
    textFieldContainer: {}
});

export interface BlocklistTextInputWithChipsProps {
    inputText: string;
    setInputText: (text: string) => void;
    terms: string[];
    setTerms: React.Dispatch<React.SetStateAction<string[]>>;
    testID?: string;
    errorMessage: string;
    setErrorMessage: (msg: string) => void;
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function BlocklistTextInputWithChips({inputText, setInputText, terms, setTerms, testID, errorMessage, setErrorMessage }: BlocklistTextInputWithChipsProps) {
    const s = BlocklistTextInputWithChipsStyle;
    
    const datastore = useDatastore();

    const [textInputHasFocus, setTextInputHasFocus] = useState<boolean>(false);
    const placeholder = useTranslation("Add terms - separate multiple terms with ;");

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
    async function onChange(text: string): Promise<void> {
        // Transform to lowercase
        let lowercaseText = text.toLowerCase();

        // Replace ";", or tab with newline
        lowercaseText = lowercaseText.replace(/(\t|;)/gm, "\n");

        // Split text into array at newline
        const splitTextArray = lowercaseText.split(/(\n)/gm);

        // Remove unnecessary spaces at the start and at the end of the term
        let cleanedTextArray: string[] = [];
        splitTextArray.forEach((splitText) => {
            cleanedTextArray.push(splitText.replace(/^ +| +$/g, ""));
        });

        // Remove empty string entries from array
        cleanedTextArray = cleanedTextArray.filter((v) => v !== "");

        // Remove duplicate entries from array
        let newTermsArray: string[] = [...new Set(cleanedTextArray)];

        // newTermsArray uses "\n" as a separating element between terms, so having 2 or more elements in newTermsArray means there is at least 1 new term to add
        if (newTermsArray.length >= 2) {
            newTermsArray = newTermsArray.filter((v) => v !== "\n");

            for (const newTerm of newTermsArray) {
                if (newTerm.length > BLOCKLIST_CHARACTER_LIMIT) {
                    setErrorMessage(
                        newTermsArray.length > 2
                            ? "Terms longer than " + BLOCKLIST_CHARACTER_LIMIT + " characters have been removed."
                            : "Terms may not be longer than " + BLOCKLIST_CHARACTER_LIMIT + " characters."
                    );
                    continue;
                }

                const isTermInBlocklist = await datastore.callServerAsync("blocklist", "findTermInBlocklist", {
                    term: newTerm,
                });

                // Check if the new term has already been entered in the input field or saved to the blocklist
                if (newTerm.length > 0 && !terms.includes(newTerm) && !isTermInBlocklist) {
                    setTerms((terms) => [...terms, newTerm]);
                    setErrorMessage("");
                    setInputText("");
                } else {
                    setErrorMessage("This term is already in the list");
                }
            }
        } else {
            setInputText(text);
            setErrorMessage("");
        }
    }

    function onDelete(text: string): void {
        const remainingTerms = [...terms.filter((term) => term !== text)];
        if (remainingTerms.length === 0) {
            setTerms([]);
        } else {
            setTerms(remainingTerms);
        }
    }

    return (
        <View>
            <View style={[
                s.inputContainer,
                textInputHasFocus ? s.inputContainerFocus : undefined,
                errorMessage ? s.inputContainerError : undefined
            ]}>
                    {terms?.length > 0 && <View style={s.termsContainer}>
                        <View style={s.termsContainerInner}>
                            {terms.map((term, index) => (
                                <View key={index} style={s.termContainer}>
                                    <CTAButton text={term} icon={<Close color={colorTextPrimary}/>} type="secondary" onPress={() => onDelete(term)} />
                                </View>
                            ))}
                        </View>
                    </View>}

                <View style={s.textFieldContainer}>
                    <AutoSizeTextInput
                        value={inputText}
                        onChange={(text) => onChange(text)}
                        emptyHeight={54}
                        autoFocus={false}
                        testID={testID}
                        style={s.textField}
                        placeholder={placeholder}
                        ariaLabel={placeholder}
                        placeholderTextColor={colorAmbiguousLightGrey}
                        onFocus={() => setTextInputHasFocus(true)}
                        onBlur={() => setTextInputHasFocus(false)}
                        multiline={true}
                        maxHeight={54}
                    />
                </View>
            </View>
            <View style={{ display: "flex", flexDirection: "column" }}>
                {errorMessage !== "" ? <UtilityText label={errorMessage} color={colorTextWarning} /> : null}
            </View>
        </View>
    );
}